import { apiRequest } from '@/api/core/client';
import type {
  TodoBootstrapPayload,
  TodoList,
  TodoProject,
  TodoTag,
  TodoTask,
  TodoTaskStatus,
  TodoPrimaryView,
  TodoViewPayload,
  TodoLogbookEntry,
} from '@/types/todo';
import type { RepeatRuleState } from '@/types/repeat';

interface CreateListInput {
  name: string;
  icon?: string | null;
  color?: string | null;
  sortKey?: number;
  metadata?: Record<string, unknown>;
}

interface CreateProjectInput {
  listId: string;
  name: string;
  icon?: string | null;
  color?: string | null;
  sortKey?: number;
  status?: 'active' | 'completed';
  hiddenUntil?: string | null;
  metadata?: Record<string, unknown>;
}

interface TaskInput {
  title: string;
  notesMarkdown?: string;
  listId?: string;
  projectId?: string | null;
  priority?: string | null;
  startDate?: string | null;
  scheduledDate?: string | null;
  dueDate?: string | null;
  isSomeday?: boolean;
  tagIds?: string[];
  emails?: { accountId: string; messageId: string }[];
  metadata?: Record<string, unknown>;
  repeat?: RepeatRuleState | Record<string, unknown> | null;
  position?: number;
  status?: TodoTaskStatus;
}

interface ReorderTaskInput {
  taskId: string;
  position?: number;
  listId?: string | null;
  projectId?: string | null;
  scheduledDate?: string | null;
  dueDate?: string | null;
  isSomeday?: boolean;
}

export type { ReorderTaskInput };

type TaskPayload = Partial<TaskInput> & {
  notes_markdown?: string;
};

function encodeTaskPayload(payload: Partial<TaskInput>): TaskPayload {
  if (!Object.prototype.hasOwnProperty.call(payload, 'notesMarkdown')) {
    return payload;
  }
  return {
    ...payload,
    notes_markdown: payload.notesMarkdown ?? '',
  };
}

export async function fetchTodoBootstrap(): Promise<TodoBootstrapPayload> {
  return apiRequest<TodoBootstrapPayload>({
    path: '/todos/bootstrap',
  });
}

export async function fetchTodoView(view: TodoPrimaryView): Promise<TodoViewPayload> {
  return apiRequest<TodoViewPayload>({
    path: `/todos/views/${view}`,
  });
}

export async function fetchTodoLists(): Promise<{ lists: TodoList[]; projects: TodoProject[] }> {
  return apiRequest<{ lists: TodoList[]; projects: TodoProject[] }>({
    path: '/todos/lists',
  });
}

export async function createTodoList(payload: CreateListInput): Promise<TodoList> {
  const { list } = await apiRequest<{ list: TodoList }>({
    path: '/todos/lists',
    method: 'POST',
    body: payload,
  });
  return list;
}

export async function updateTodoList(listId: string, payload: Partial<CreateListInput>): Promise<TodoList> {
  const { list } = await apiRequest<{ list: TodoList }>({
    path: `/todos/lists/${listId}`,
    method: 'PATCH',
    body: payload,
  });
  return list;
}

export async function reorderTodoLists(updates: Array<{ id: string; sortKey: number }>): Promise<TodoList[]> {
  const { lists } = await apiRequest<{ lists: TodoList[] }>({
    path: '/todos/lists/reorder',
    method: 'POST',
    body: { updates },
  });
  return lists;
}

export async function deleteTodoList(listId: string) {
  return apiRequest<{ deleted: TodoList | null; fallbackListId: string | null }>({
    path: `/todos/lists/${listId}`,
    method: 'DELETE',
  });
}

export async function createTodoProject(payload: CreateProjectInput): Promise<TodoProject> {
  const { project } = await apiRequest<{ project: TodoProject }>({
    path: '/todos/projects',
    method: 'POST',
    body: payload,
  });
  return project;
}

export async function updateTodoProject(projectId: string, payload: Partial<CreateProjectInput>): Promise<TodoProject> {
  const { project } = await apiRequest<{ project: TodoProject }>({
    path: `/todos/projects/${projectId}`,
    method: 'PATCH',
    body: payload,
  });
  return project;
}

export async function reorderTodoProjects(updates: Array<{ id: string; sortKey: number }>): Promise<TodoProject[]> {
  const { projects } = await apiRequest<{ projects: TodoProject[] }>({
    path: '/todos/projects/reorder',
    method: 'POST',
    body: { updates },
  });
  return projects;
}

export async function deleteTodoProject(projectId: string): Promise<TodoProject | null> {
  const { project } = await apiRequest<{ project: TodoProject | null }>({
    path: `/todos/projects/${projectId}`,
    method: 'DELETE',
  });
  return project;
}

export async function fetchTodoTags(): Promise<TodoTag[]> {
  const { tags } = await apiRequest<{ tags: TodoTag[] }>({
    path: '/todos/tags',
  });
  return tags;
}

export async function createTodoTag(payload: { name: string; color?: string | null }): Promise<TodoTag> {
  const { tag } = await apiRequest<{ tag: TodoTag }>({
    path: '/todos/tags',
    method: 'POST',
    body: payload,
  });
  return tag;
}

export async function updateTodoTag(tagId: string, payload: { name?: string; color?: string | null }): Promise<TodoTag> {
  const { tag } = await apiRequest<{ tag: TodoTag }>({
    path: `/todos/tags/${tagId}`,
    method: 'PATCH',
    body: payload,
  });
  return tag;
}

export async function deleteTodoTag(tagId: string): Promise<TodoTag | null> {
  const { tag } = await apiRequest<{ tag: TodoTag | null }>({
    path: `/todos/tags/${tagId}`,
    method: 'DELETE',
  });
  return tag;
}

export async function createTodoTask(payload: TaskInput): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: '/todos/tasks',
    method: 'POST',
    body: encodeTaskPayload(payload),
  });
  return task;
}

export async function updateTodoTask(taskId: string, payload: Partial<TaskInput>): Promise<{ task: TodoTask; spawnedTask: TodoTask | null }> {
  return apiRequest<{ task: TodoTask; spawnedTask: TodoTask | null }>({
    path: `/todos/tasks/${taskId}`,
    method: 'PATCH',
    body: encodeTaskPayload(payload),
  });
}

export async function reorderTodoTasks(updates: ReorderTaskInput[]): Promise<TodoTask[]> {
  const { tasks } = await apiRequest<{ tasks: TodoTask[] }>({
    path: '/todos/tasks/reorder',
    method: 'POST',
    body: { updates },
  });
  return tasks;
}

export async function updateTodoTaskStatus(taskId: string, status: TodoTaskStatus): Promise<{ task: TodoTask; spawnedTask: TodoTask | null }> {
  return apiRequest<{ task: TodoTask; spawnedTask: TodoTask | null }>({
    path: `/todos/tasks/${taskId}/status`,
    method: 'POST',
    body: { status },
  });
}

export async function bulkUpdateTodoTaskStatus(
  status: TodoTaskStatus,
  taskIds: string[]
): Promise<Array<{ task: TodoTask; spawnedTask: TodoTask | null }>> {
  const { results } = await apiRequest<{ results: Array<{ task: TodoTask; spawnedTask: TodoTask | null }> }>({
    path: '/todos/tasks/bulk-status',
    method: 'POST',
    body: { status, taskIds },
  });
  return results;
}

export async function updateTodoTaskTags(taskId: string, tagIds: string[]): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}/tags`,
    method: 'POST',
    body: { tagIds },
  });
  return task;
}

export async function toggleTodoTaskSomeday(taskId: string, isSomeday: boolean): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}/someday`,
    method: 'PATCH',
    body: { isSomeday },
  });
  return task;
}

export async function attachTodoTaskEmail(taskId: string, payload: { accountId: string; messageId: string }): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}/emails`,
    method: 'POST',
    body: payload,
  });
  return task;
}

export async function detachTodoTaskEmail(taskId: string, payload: { accountId: string; messageId: string }): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}/emails`,
    method: 'DELETE',
    body: payload,
  });
  return task;
}

export async function fetchTasksForEmail(accountId: string, messageId: string): Promise<TodoTask[]> {
  const { tasks } = await apiRequest<{ tasks: TodoTask[] }>({
    path: `/todos/email/${accountId}/${messageId}`,
  });
  return tasks;
}

export async function fetchTodoTask(taskId: string): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}`,
  });
  return task;
}

export async function purgeTodoTask(taskId: string): Promise<TodoTask> {
  const { task } = await apiRequest<{ task: TodoTask }>({
    path: `/todos/tasks/${taskId}`,
    method: 'DELETE',
  });
  return task;
}

export async function searchTodoTasks(query: string): Promise<TodoTask[]> {
  const { results } = await apiRequest<{ results: TodoTask[] }>({
    path: '/todos/search',
    query: { q: query },
  });
  return results;
}

export async function fetchTodoLogbook(): Promise<TodoLogbookEntry[]> {
  const { entries } = await apiRequest<{ entries: TodoLogbookEntry[] }>({
    path: '/todos/logbook',
  });
  return entries;
}

export async function fetchTodoTrash(): Promise<TodoTask[]> {
  const { tasks } = await apiRequest<{ tasks: TodoTask[] }>({
    path: '/todos/trash',
  });
  return tasks;
}

export interface SimilarTask {
  taskId: string;
  title: string;
  notesMarkdown?: string;
  status: string;
  priority?: string;
  similarity: number;
}

export async function getSimilarTasks(taskId: string, limit: number = 5): Promise<SimilarTask[]> {
  const { similar } = await apiRequest<{ similar: SimilarTask[] }>({
    path: `/todos/tasks/${taskId}/similar`,
    query: { limit },
  });
  return similar;
}

export async function getRelatedTasksForEmail(accountId: string, messageId: string, limit: number = 5): Promise<{ task: TodoTask; similarity: number }[]> {
  const { tasks } = await apiRequest<{ tasks: { task: TodoTask; similarity: number }[] }>({
    path: `/todos/email/${accountId}/${messageId}/related`,
    query: { limit },
  });
  return tasks;
}
